/**
 * Ventilation tenders Admin — miroir de backend/services/paiementTenderBreakdown.js
 * Source : commande.paiements[] (ledger), pas methodePaiement scalar.
 */

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function normalizeMethode(raw) {
  return String(raw || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]+/g, '');
}

function tenderLabel(methodePaiement) {
  const n = normalizeMethode(methodePaiement);
  if (n === 'GIFTCARD' || n === 'CARTECADEAU') return 'Carte cadeau';
  if (n === 'CARTE') return 'Carte bancaire';
  if (n === 'ESPECES') return 'Espèces';
  if (n === 'CHEQUE') return 'Chèque';
  if (n === 'VIREMENT') return 'Virement';
  if (n === 'GOOGLEPAY') return 'Google Pay';
  if (n === 'APPLEPAY') return 'Apple Pay';
  return methodePaiement ? String(methodePaiement) : 'Paiement';
}

/** Priorité : providerMetadata.walletType → modePaiement → null (fallback CARTE). */
function resolveCardChannelDisplayLabel(paiement, modePaiement) {
  const meta =
    paiement?.providerMetadata && typeof paiement.providerMetadata === 'object'
      ? paiement.providerMetadata
      : null;
  const wt = String(meta?.walletType || '')
    .trim()
    .toUpperCase()
    .replace(/[_\s-]+/g, '_');
  if (wt === 'GOOGLE_PAY' || wt === 'GOOGLEPAY') return 'Google Pay';
  if (wt === 'APPLE_PAY' || wt === 'APPLEPAY') return 'Apple Pay';

  const mode = String(modePaiement || '')
    .trim()
    .toLowerCase();
  if (mode === 'googlepay') return 'Google Pay';
  if (mode === 'applepay') return 'Apple Pay';
  return null;
}

/**
 * Libellé canal carte pour Admin (commerçant / super-admin) — priorité modePaiement.
 * Ne dépend pas de providerMetadata.
 * @param {string|null|undefined} modePaiement
 * @returns {'Google Pay'|'Apple Pay'|'Carte bancaire'}
 */
export function cardChannelLabelFromMode(modePaiement) {
  return resolveCardChannelDisplayLabel(null, modePaiement) || 'Carte bancaire';
}

/** Modes carte en ligne (Stripe), y compris wallets. */
export function isOnlineCardMode(modePaiement, methodePaiement) {
  const mode = String(modePaiement || '')
    .trim()
    .toLowerCase();
  if (['enligne', 'googlepay', 'applepay', 'paypal'].includes(mode)) return true;
  return String(methodePaiement || '').toUpperCase() === 'CARTE';
}

function isGiftMethode(methodePaiement) {
  const n = normalizeMethode(methodePaiement);
  return n === 'GIFTCARD' || n === 'CARTECADEAU';
}

function isStripeCartePaiement(p) {
  if (isGiftMethode(p?.methodePaiement)) return false;
  const n = normalizeMethode(p?.methodePaiement);
  if (n === 'CARTE') return true;
  const pi = p?.providerTransactionId;
  return Boolean(pi && String(pi).startsWith('pi_'));
}

export function buildTenderBreakdown(paiements = [], opts = {}) {
  const list = Array.isArray(paiements) ? paiements : [];
  const modePaiement = opts?.modePaiement;
  const buckets = new Map();

  for (const p of list) {
    const statut = String(p?.statutPaiement || '').toUpperCase();
    const montant = Number(p?.montant);
    if (statut !== 'VALIDE' || !Number.isFinite(montant) || montant <= 0) continue;

    let methode = p?.methodePaiement || 'OTHER';
    if (isGiftMethode(methode)) methode = 'GIFT_CARD';
    else if (isStripeCartePaiement(p)) methode = 'CARTE';

    const key = normalizeMethode(methode) || 'OTHER';
    const prev = buckets.get(key) || { methode: key, amount: 0, sample: p };
    prev.amount = roundMoney(prev.amount + montant);
    if (!prev.sample) prev.sample = p;
    const sampleWt = prev.sample?.providerMetadata?.walletType;
    const nextWt = p?.providerMetadata?.walletType;
    if (!sampleWt && nextWt) prev.sample = p;
    buckets.set(key, prev);
  }

  const lines = [];
  let giftAmount = 0;
  let stripeAmount = 0;

  for (const { methode, amount, sample } of buckets.values()) {
    const net = roundMoney(amount);
    if (net <= 0.009) continue;
    const displayMethode = isGiftMethode(methode) ? 'GIFT_CARD' : methode;
    let label = tenderLabel(displayMethode);
    if (normalizeMethode(displayMethode) === 'CARTE') {
      const channelLabel = resolveCardChannelDisplayLabel(sample, modePaiement);
      if (channelLabel) label = channelLabel;
    }
    lines.push({
      methode: displayMethode,
      label,
      amount: net,
    });
    if (isGiftMethode(displayMethode)) giftAmount = roundMoney(giftAmount + net);
    else if (normalizeMethode(displayMethode) === 'CARTE') {
      stripeAmount = roundMoney(stripeAmount + net);
    }
  }

  const rank = (m) => {
    if (isGiftMethode(m)) return 0;
    if (normalizeMethode(m) === 'CARTE') return 1;
    return 2;
  };
  lines.sort((a, b) => rank(a.methode) - rank(b.methode) || a.label.localeCompare(b.label, 'fr'));

  return {
    lines,
    totalPaid: roundMoney(lines.reduce((s, l) => s + l.amount, 0)),
    giftAmount,
    stripeAmount,
    isMixed: lines.length > 1,
    hasGift: giftAmount > 0.009,
    hasStripe: stripeAmount > 0.009,
  };
}

export function hasGiftTender(cmd) {
  return buildTenderBreakdown(cmd?.paiements || [], {
    modePaiement: cmd?.modePaiement,
  }).hasGift;
}

export function isOnlinePaidPriceLocked(cmd) {
  const mode = String(cmd?.modePaiement || '').toLowerCase();
  const online = ['enligne', 'paypal', 'applepay', 'googlepay'].includes(mode);
  if (!online) return false;
  const statut = String(cmd?.statutPaiement || '').toUpperCase();
  if (statut === 'VALIDE' || statut === 'PAYE') return true;
  return (cmd?.paiements || []).some(
    (p) => String(p?.statutPaiement || '').toUpperCase() === 'VALIDE' && Number(p?.montant) > 0
  );
}

/** Aligné backend commandePoidsAjustement / paiementTenderBreakdown. */
export function isVariableWeightLine(ligne) {
  return Boolean(
    ligne?.produit?.venteAuPoids ||
      ligne?.poidsSouhaite ||
      ligne?.poidsReel ||
      ligne?.typeDecoupe
  );
}

export function commandeHasVariableWeightLines(cmd) {
  return (cmd?.produits || []).some(isVariableWeightLine);
}

/**
 * Lock prix fixe online — exception pour mutation poids réel.
 * @param {object} cmd
 * @param {{ mutation?: 'fixed_price'|'variable_weight_adjust'|'line_unavailable' }} [opts]
 */
export function isOnlinePaidPriceLockedForMutation(cmd, opts = {}) {
  if (!isOnlinePaidPriceLocked(cmd)) return false;
  const mutation = opts.mutation || 'fixed_price';
  if (
    mutation === 'variable_weight_adjust' &&
    commandeHasVariableWeightLines(cmd)
  ) {
    return false;
  }
  return true;
}

export const PRICE_LOCK_MESSAGE =
  'Cette commande a déjà été encaissée. Le montant ne peut plus être modifié directement. Utilisez un remboursement si vous devez réduire le montant de la commande.';

export const VARIABLE_WEIGHT_LOCK_EXCEPTION_HINT =
  'Les lignes à prix fixe restent verrouillées. Vous pouvez encore saisir le poids réel des produits vendus au poids ; le total sera recalculé côté serveur.';
